import React, { useCallback, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import uuidV4 from 'lib/string/uuidV4Utils'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import { getOfferListKeySelector } from 'selectors/offerSelectors'
import { isLastMinuteEnabled } from 'selectors/lastMinute'
import PartnershipBanner from 'components/Common/Vendors/Partnership/PartnershipBanner'
import Leaderboard from 'components/Common/Banner/Leaderboard'
import LastMinuteYAMLOfferList from './LastMinuteYAMLOfferList'
import FrontPage from './FrontPage'
import { fetchListBanners } from 'actions/ContentActions'
import { replace } from 'connected-react-router'
import { setSearchParamValue } from 'lib/url/searchUrlUtils'
import * as InteractionStudioService from 'api/interactionStudio'
import * as Analytics from 'analytics/analytics'
import { FLASH_AND_TAO_OFFERS_TYPES, OLD_HOME_PAGE_LIMIT } from 'constants/offerList'
import TextLink from 'components/Luxkit/TextLink'
import InlineGroup from 'components/Common/InlineGroup'
import Spacing from 'components/Common/Spacing'
import { FrontPageContainer, SpacedOfferList } from './common'
import OfferListFrontPageFilters from 'components/OfferList/OfferListFilter/OfferListFrontPageFilters'
import FrontPageDiscoverMoreDestinationLinks from './FrontPageDiscoverMoreDestinationLinks'
import { BANNER_LOCATION_HOME } from 'constants/bannerLocations'
import { removedGenesysFromStorage } from 'analytics/genesys'
import { getListName, productClickSnowplow, productImpressionSnowplow } from 'analytics/enhanced-ecommerce'
import { productClickObject, productImpressionObject } from 'analytics/eventDefinitions'
import { OfferListEvents, OfferListEventsProvider } from 'components/OfferList/v2/OfferListEventsContext'
import { OfferListV2Item } from 'components/OfferList/v2/OfferList'
import BannerCarousel from 'components/Common/Banner/BannerCarousel'
import FrontPageHighIntentOffers from './FrontPageHighIntentOffers'
import { queryKeyMapPinCount } from 'constants/url'
import { HOMEPAGE_MAX_MAP_PINS } from 'constants/search'
import { clickHomepageTileEvent } from 'analytics/optimizely/events'
import HomepagePartnerBrands from 'components/Common/PartnerBrands/HomepagePartnerBrands'
import config from 'constants/config'
import FrontPageUSPComponent from './USP/FrontPageUSPComponent'
import styled from 'styled-components'
import TrustIndicatorSection from 'home/components/TrustIndicatorSection/TrustIndicatorSection'
import { rem } from 'polished'
import { getHomeOfferListParams } from 'home/helpers'
import Divider from 'components/Luxkit/Divider'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import { getHighIntentOffers } from 'selectors/recomendations/highIntentSelectors'
import { SearchHeader } from 'components/Search'
import { isSearchEnabled } from 'lib/search/searchUtils'
import { isBestOfCampaignEnabled } from 'selectors/featuresSelectors'

const Sheet = styled.div`
  padding: ${rem(40)} 0;
  background-color: ${props => props.theme.palette.neutral.eight};
`

interface Props {
  regionCode: string;
  subregion: string;
  filters: App.OfferListFilters;
  partnership: App.Partnership;
  lastMinuteEnabled: boolean;
  availableFilters: App.OfferListAvailableFilters;
  banners: Array<App.ListBanner>;
  searchQuery: string;
  currency: string;
}

const ANALYTICS_FRONT_PAGE_GUEST_TAG = 'FPG'

function FrontPageGuest(props: Props) {
  const {
    filters,
    partnership,
    regionCode,
    subregion,
    banners,
    availableFilters,
    lastMinuteEnabled,
    searchQuery,
    currency,
  } = props

  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchListBanners(BANNER_LOCATION_HOME))
    // eslint-disable-next-line
  }, [])

  const onBannerClick = useCallback(() => {
    Analytics.track('click-homepage-tile', clickHomepageTileEvent())
  }, [])

  useEffect(() => {
    if (removedGenesysFromStorage()) window.location.reload()
  }, [])

  const listId = useMemo(() => uuidV4(), [])
  const listName = getListName('OfferList', ANALYTICS_FRONT_PAGE_GUEST_TAG, 'Main Offer List')
  const highIntentOffers = useAppSelector(getHighIntentOffers)
  const hasHighIntentOffers = highIntentOffers.length > 0

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialPage = useMemo(() => parseInt(new URLSearchParams(searchQuery).get('pages') ?? '1'), [])

  const offerListExtras: Array<OfferListV2Item> = [
    {
      id: 'partnerhsip',
      position: 'start',
      element: <PartnershipBanner bannerId="homepageBanner" />,
      show: !!partnership && regionCode !== 'AU' && regionCode !== 'GB' && regionCode !== 'US',
    },
    {
      id: 'list-p1',
      position: 1,
      element: <BannerCarousel banners={banners.slice(0, 3)} onBannerClick={onBannerClick} />,
      show: !!banners[0],
    },
    {
      id: 'partner-brands',
      position: 3,
      element: <HomepagePartnerBrands />,
      show: config.logoImages && config.logoImages.length > 0,
    },
    {
      id: 'list-p4',
      position: 4,
      element: <Leaderboard banners={[banners[3]]} />,
      show: !!banners[3],
    },
    {
      id: 'list-p8',
      position: 8,
      element: <Leaderboard banners={[banners[4]]} />,
      show: !!banners[4],
    },
    {
      id: 'list-p12',
      position: 12,
      element: <Leaderboard banners={[banners[5]]} />,
      show: !!banners[5],
    },
    {
      position: 'end',
      id: 'LME-YMAL',
      element: <LastMinuteYAMLOfferList offerListFilters={filters} />,
      show: lastMinuteEnabled,
    },
    {
      position: 'end',
      id: 'discovermore',
      element: <FrontPageDiscoverMoreDestinationLinks
        filteredLocations={filters.locations}
        availableFilters={availableFilters}
      />,
      show: (filters.locations?.length ?? 0) > 0,
    },
  ]

  const onFetchMore = useCallback((page: number) => {
    dispatch(replace({ search: setSearchParamValue(searchQuery, 'pages', page) }))
  }, [dispatch, searchQuery])

  const onAnalyticEvent = useCallback((action: OfferListEvents, data: any) => {
    if (action === OfferListEvents.productClick) {
      const { offer, index } = data
      InteractionStudioService.trackOfferListClick({
        offerId: offer.id,
        index,
        list: 'homepage',
      })
      productClickSnowplow(currency, productClickObject(offer, index + 1), { listId, listName })
      Analytics.track('click-homepage-tile', clickHomepageTileEvent())
    } else if (action === OfferListEvents.impression) {
      const { offer, index } = data
      productImpressionSnowplow(
        currency,
        [productImpressionObject(offer, listName, index + 1)],
        {
          listId,
          listName,
        },
      )
    }
  }, [currency, listName, listId])

  const offerListFilters = useMemo(() => {
    return {
      ...getHomeOfferListParams({
        region: regionCode,
        subregion,
        filters,
      }),
      limit: OLD_HOME_PAGE_LIMIT,
    }
  }, [filters, regionCode, subregion])

  const mapViewPath = `/search/map?${setSearchParamValue('', queryKeyMapPinCount, HOMEPAGE_MAX_MAP_PINS)}`
  const isBestOfCampaign = useAppSelector(isBestOfCampaignEnabled)
  const searchHeaderTitle = useMemo(() => isBestOfCampaign ? 'We\'re celebrating ten years of the world\'s best escapes' : config.searchFormSection.title, [isBestOfCampaign])

  return (<FrontPage>
    {isSearchEnabled() && <SearchHeader title={searchHeaderTitle}/>}
    <Sheet>
      <LayoutContainer>
        <VerticalSpacer gap={40}>
          {hasHighIntentOffers && config.LERE_HIGH_INTENT_CAROUSEL_ENABLED && <ErrorBoundary fallback={null}>
            <FrontPageHighIntentOffers highIntentOffers={highIntentOffers} />
            <Divider kind="primary" />
          </ErrorBoundary>}
          <FrontPageUSPComponent />
          <Divider kind="primary" />
          <TrustIndicatorSection />
        </VerticalSpacer>
      </LayoutContainer>
    </Sheet>
    <FrontPageContainer>
      <OfferListFrontPageFilters
        existingFilters={filters}
        types={FLASH_AND_TAO_OFFERS_TYPES}
        mapViewPath={mapViewPath}
      />
      <OfferListEventsProvider onListEvent={onAnalyticEvent}>
        <SpacedOfferList
          filters={offerListFilters}
          interstitials={offerListExtras}
          onPageChange={onFetchMore}
          defaultPage={initialPage}
          eagerLoadFirstImage
        />
      </OfferListEventsProvider>
      {(availableFilters?.total ?? 20) > 12 && <InlineGroup horizontal="center">
        <Spacing mt={16} mb={16}>
          <TextLink size="large" to="/page/2" rel="next">See more offers</TextLink>
        </Spacing>
      </InlineGroup>}
    </FrontPageContainer>
  </FrontPage>)
}

FrontPageGuest.defaultProps = {
  banners: [],
}

const mapStateToProps = (state: App.State) => {
  const key = getOfferListKeySelector(state, {
    types: FLASH_AND_TAO_OFFERS_TYPES,
  })

  return ({
    currency: state.geo.currentCurrency,
    regionCode: state.geo.currentRegionCode,
    subregion: state.geo.geoStateName,
    partnership: state.geo.partnership,
    filters: getUrlOfferListFilters(state),
    availableFilters: state.offer.offerListFilterOptions[key]?.filters,
    banners: state.content.listBanners?.home,
    lastMinuteEnabled: isLastMinuteEnabled(state),
    searchQuery: state.router.location.search,
  })
}

export default connect(mapStateToProps)(FrontPageGuest)
